/**
 * Hook principal pour gérer l'accès et les capabilities
 * Point d'entrée unique pour vérifier les autorisations dans l'UI
 */

import { useAccessStore } from '@sphere/auth/data-access';
import type { BaseCapabilities } from '@sphere/shared/domain';
import type { UserInfo, UserScope } from '@sphere/auth/domain';

export interface UseAccessReturn {
  // Vérification de rôles
  isAdmin: boolean;
  hasRole: (role: string) => boolean;
  hasAnyRole: (roles: string[]) => boolean;
  hasAllRoles: (roles: string[]) => boolean;

  // Vérification de sections
  isSectionVisible: (sectionKey: string) => boolean;

  // Vérification de capabilities globales (list/create)
  canList: (resourceKey: string) => boolean;
  canCreate: (resourceKey: string) => boolean;

  // Vérification de capabilities sur une ressource spécifique
  can: <T extends BaseCapabilities>(
    resource: { capabilities?: T } | null | undefined,
    capabilityKey: keyof T
  ) => boolean;

  // Helpers pour scope
  scope: UserScope;

  // État global
  authenticated: boolean;
  user: UserInfo | null;
  partial: boolean;
}

/**
 * Hook pour accéder aux fonctionnalités d'autorisation
 *
 * @example
 * ```tsx
 * const { can, hasRole, isSectionVisible } = useAccess()
 *
 * // Vérifier un rôle
 * if (hasRole('admin')) { ... }
 *
 * // Vérifier une section
 * if (isSectionVisible('analytics')) { ... }
 *
 * // Vérifier une capability sur une ressource
 * if (can(activity, 'canEdit')) {
 *   <Button onClick={handleEdit}>Modifier</Button>
 * }
 * ```
 */
export function useAccess(): UseAccessReturn {
  const authenticated = useAccessStore((state) => state.authenticated);
  const user = useAccessStore((state) => state.user);
  const isAdmin = useAccessStore((state) => state.isAdmin());
  const hasRole = useAccessStore((state) => state.hasRole);
  const hasAnyRole = useAccessStore((state) => state.hasAnyRole);
  const hasAllRoles = useAccessStore((state) => state.hasAllRoles);
  const isSectionVisible = useAccessStore((state) => state.isSectionVisible);
  const canList = useAccessStore((state) => state.canList);
  const canCreate = useAccessStore((state) => state.canCreate);
  const scope = useAccessStore((state) => state.scope);
  const partial = useAccessStore((state) => state.partial);

  /**
   * Vérifie si une capability spécifique est accordée sur une ressource
   * Retourne false si la ressource ou les capabilities sont nulles/undefined
   * Les admins ont toujours accès à toutes les capabilities
   */
  const can = <T extends BaseCapabilities>(
    resource: { capabilities?: T } | null | undefined,
    capabilityKey: keyof T
  ): boolean => {
    // Les admins ont toutes les capabilities
    if (isAdmin) {
      return true;
    }

    if (!resource || !resource.capabilities) {
      return false;
    }

    const value = resource.capabilities[capabilityKey];
    return value === true;
  };

  return {
    isAdmin,
    hasRole,
    hasAnyRole,
    hasAllRoles,
    isSectionVisible,
    canList,
    canCreate,
    can,
    scope,
    authenticated,
    user,
    partial,
  };
}

/**
 * Hook spécialisé pour vérifier rapidement si une section est visible
 */
export function useSectionVisible(sectionKey: string): boolean {
  return useAccessStore((state) => state.isSectionVisible(sectionKey));
}

/**
 * Hook spécialisé pour vérifier rapidement si l'utilisateur a un rôle
 */
export function useHasRole(role: string): boolean {
  return useAccessStore((state) => state.hasRole(role));
}

/**
 * Hook spécialisé pour vérifier rapidement si l'utilisateur a au moins un des rôles
 */
export function useHasAnyRole(roles: string[]): boolean {
  return useAccessStore((state) => state.hasAnyRole(roles));
}

/**
 * Hook pour accéder au scope utilisateur (établissements et classes)
 */
export function useUserScope() {
  return useAccessStore((state) => state.scope);
}

/**
 * Hook pour vérifier si le bootstrap est en mode dégradé (partial)
 */
export function useIsPartialMode(): boolean {
  return useAccessStore((state) => state.partial);
}

/**
 * Hook pour vérifier si l'utilisateur est administrateur
 */
export function useIsAdmin(): boolean {
  return useAccessStore((state) => state.isAdmin());
}
